/**
 * Truncated phone cell for stats table.
 * Phone on left, source_id on right. Hover: Phone, Source, Tags (StatsHoverPopover).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';

import { LeadsListTags } from '@/features/smswiser-campaigns/components/shared';
import { useToast } from '@/providers/ToastProvider';

import {
  StatsHoverPopover,
  StatsTooltipSection,
  StatsTooltipTagsBlock,
} from '../../../StatsHoverPopover';
import { truncateEnd } from '../../../shared';

interface TruncatedPhoneCellProps {
  phone: string;
  sourceId?: string | null;
  /** Performance tags from phone-performance-tags API */
  performanceTags?: string[];
  /** Tag + date range for hover tooltip */
  performanceTagDetails?: Array<{ tag: string; dateRangePreset: string }>;
  /** Custom colors for tags (rule name -> hex) */
  tagColors?: Record<string, string>;
}

export const TruncatedPhoneCell: FC<TruncatedPhoneCellProps> = ({
  phone,
  sourceId,
  performanceTags,
  performanceTagDetails,
  tagColors,
}) => {
  const { success: toastSuccess, error: toastError } = useToast();
  const displayPhone = phone || '—';
  const truncatedPhone = truncateEnd(displayPhone, 24);
  const displaySource = sourceId || '—';

  const handleCopyPhone = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!phone) return;
    navigator.clipboard
      .writeText(phone)
      .then(() => toastSuccess('Phone copied', 'Copied to clipboard'))
      .catch(() => toastError('Copy failed', 'Could not copy to clipboard'));
  };

  const handleCopySource = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!sourceId) return;
    navigator.clipboard
      .writeText(sourceId)
      .then(() => toastSuccess('Source ID copied', 'Copied to clipboard'))
      .catch(() => toastError('Copy failed', 'Could not copy to clipboard'));
  };

  const tagsLine = (performanceTags?.length ?? 0) > 0 ? (
    <LeadsListTags
      performanceTags={performanceTags ?? []}
      tagColors={tagColors}
      className="inline-flex ml-1"
      compact
      disableTooltip
    />
  ) : null;

  const hoverContent = (
    <div className="flex flex-col gap-1.5 text-left">
      <StatsTooltipSection label="Phone" valueClassName="font-mono">
        {displayPhone}
      </StatsTooltipSection>
      <StatsTooltipSection label="Source ID" valueClassName="font-mono">
        {displaySource}
      </StatsTooltipSection>
      {((performanceTagDetails?.length ?? performanceTags?.length) ?? 0) > 0 && (
        <div>
          <StatsTooltipTagsBlock
            performanceTags={performanceTags}
            performanceTagDetails={performanceTagDetails}
            tagColors={tagColors}
          />
        </div>
      )}
    </div>
  );

  return (
    <StatsHoverPopover content={hoverContent}>
      <div className="flex min-w-0 flex-1 items-baseline gap-2 text-xs text-muted-foreground cursor-default">
        <button
          type="button"
          onClick={handleCopyPhone}
          title="Click to copy phone"
          className="min-w-0 truncate text-left font-mono font-medium text-foreground cursor-pointer rounded px-0.5 -mx-0.5 hover:bg-muted/60 hover:text-primary"
          aria-label="Copy phone"
        >
          {truncatedPhone.display}
        </button>
        <button
          type="button"
          onClick={handleCopySource}
          title="Click to copy source ID"
          className="shrink-0 ml-auto font-mono tabular-nums font-medium text-foreground/80 cursor-pointer rounded px-0.5 -mx-0.5 hover:bg-muted/60 hover:text-primary"
          aria-label="Copy source ID"
        >
          {displaySource}
        </button>
        {tagsLine}
      </div>
    </StatsHoverPopover>
  );
};
